/**
 * Touch svg web component example.
 * 
 * <touch-svg image="url to image"></touch-svg>
 * 
 * @author Stephen Paul Hassall
 * @license https://CodeRunDebug.com/license.html
 * @web https://CodeRunDebug.com
 */

import TouchTools from "./touch-tools.js";

class TouchSvg extends HTMLElement {
    /**
     * Constructor function.
     */
    constructor() {
        // Must call super first
        super();

        // Attach shadow DOM root
        this.attachShadow({mode: 'open'});

        // Set styles
        this.style.backgroundColor = '#F1F1F1';

        // Set touch event function binding
        this._touchStartEvent = this._touchStartEvent.bind(this);
        this._touchMoveEvent = this._touchMoveEvent.bind(this);

        // Set pointer (non-touch device) event function binding
        this._pointerDownEvent = this._pointerDownEvent.bind(this);
        this._pointerUpEvent = this._pointerUpEvent.bind(this);
        this._pointerMoveEvent = this._pointerMoveEvent.bind(this);

        // Set touch tools
        this._touchTools = new TouchTools();

        // Set touch element
        this._touchTools.setTouchElement(this);
    }

    /**
    * Override connectedCallback function to handle when component is attached into the DOM.
    * @override
    */
    connectedCallback() {
        // Add touch events
        this.addEventListener('touchstart', this._touchStartEvent, { passive: true });
        this.addEventListener('touchmove', this._touchMoveEvent, { passive: true });

        // Add pointer events
        this.addEventListener('pointerdown', this._pointerDownEvent, { passive: true });
        this.addEventListener('pointerup', this._pointerUpEvent, { passive: true });
        this.addEventListener('pointermove', this._pointerMoveEvent, { passive: true });
    }

    /**
     * Override disconnectedCallback function to handle when component is detached from the DOM.
     * @override
     */
    disconnectedCallback() {
        // Remove touch events
        this.removeEventListener('touchstart', this._touchStartEvent, { passive: true });
        this.removeEventListener('touchmove', this._touchMoveEvent, { passive: true });

        // Remove pointer events
        this.removeEventListener('pointerdown', this._pointerDownEvent, { passive: true });
        this.removeEventListener('pointerup', this._pointerUpEvent, { passive: true });
        this.removeEventListener('pointermove', this._pointerMoveEvent, { passive: true });
    }

    /**
     * Override attributeChangedCallback function to handle attribute changes
     * @param {string} name Then name of the attribute that has changed.
     * @param {string} oldValue The old value of the attribute before it was changed.
     * @param {string} newValue The new value the attribute is being changed to.
     * @override
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // Load the new image
        this._loadImage();
    }

    /**
     * Override the observedAttributes function to return the list
     * of attributes to monitor.
     * @return {Array} List of attribute names.
     * @static
     * @override
     */
    static get observedAttributes() {
        // Return the list of attributes
        return ['image'];
    }

    /**
     * Load the image, set some starting values and draw into the canvas.
     */
    _loadImage() {
        // Get the SVG image as text
        fetch(this.getAttribute('image'))
        .then(r => r.text())
        .then(text => {
            // Add the SVG to the shadow DOM
            this.shadowRoot.innerHTML = text;

            // Get the SVG element
            this._svgElement = this.shadowRoot.querySelector('svg');

            // Remove any width or height attributes
            this._svgElement.removeAttribute('width');
            this._svgElement.removeAttribute('height');

            // Add some extra styling to scale the SVG to fit within the shadow DOM
            this._svgElement.style.display = 'block';
            this._svgElement.style.height = '100%';

            // Workout the starting zoom amount
            let startingZoom = 1;

            // Check the client's height/width and the SVG's height/width
            if (this.clientHeight > this.clientWidth) {
                if (this._svgElement.viewBox.baseVal.height > this._svgElement.viewBox.baseVal.width) {
                    startingZoom = 1;
                } else {
                    startingZoom = this.clientWidth / this._svgElement.clientWidth;
                }
            } else {
                if (this._svgElement.viewBox.baseVal.height > this._svgElement.viewBox.baseVal.width) {
                    startingZoom = this.clientHeight / this._svgElement.clientHeight;
                } else {
                    startingZoom = this.clientWidth / this._svgElement.clientWidth;
                }
            }

            // Set starting coordinate space adjustments
            this._touchTools.setCoordinateSpace(0, 0, startingZoom, 0);

            // Adjust SVG
            this._adjustSvg();
        });
    }

    /**
     * Touch start event.
     * @param {object} event The touch start event data.
     */
    _touchStartEvent(event) {
        // Call touch start event with list of touches
        this._touchTools.touchStart(event.touches);
    }

    /**
     * Touch move event.
     * @param {object} event The touch move event data.
     */
    _touchMoveEvent(event) {
        // Call touch move event with list of touches
        this._touchTools.touchMove(event.touches);        

        // Draw the image in the canvas (after the touch adjustments are made)
        this._adjustSvg();
    }

    /**
     * Pointer down event.
     * @param {Object} event The pointer down event data.
     */
    _pointerDownEvent(event) {
        // Call pointer down event
        this._touchTools.pointerDown(event);
    }

    /**
     * Pointer up event.
     * @param {Object} event The pointer up event data.
     */
    _pointerUpEvent(event) {
        // Call pointer up event
        this._touchTools.pointerUp(event);
    }

    /**
     * Pointer move event.
     * @param {Object} event The pointer move event data.
     */
    _pointerMoveEvent(event) {
        // Call pointer move event
        this._touchTools.pointerMove(event);

        // Draw the image in the canvas (after the touch adjustments are made)
        this._adjustSvg();
    }

    /**
     * Adjust the SVG using the coordinate space adjustment information.
     */
    _adjustSvg() {
        // Set rotate in dagress
        const degrees = this._touchTools.coordinateSpaceRotate * 180 / Math.PI;

        // Set center adjustments
        let centerAdjustmentX = (this.clientWidth - this._svgElement.clientWidth) / 2;
        let centerAdjustmentY = (this.clientHeight - this._svgElement.clientHeight) / 2;

        // Set transform text
        const transformText = 
            'translate(' + centerAdjustmentX.toString() + ' ' + centerAdjustmentY.toString() + ') ' +
            'scale(' + this._touchTools.coordinateSpaceScale.toString() + ' ' + this._touchTools.coordinateSpaceScale.toString() + ') ' +
            'translate(' + this._touchTools.coordinateSpaceX.toString() + ' ' + -this._touchTools.coordinateSpaceY.toString() + ') ' +
            'rotate(' + degrees.toString() + ')';

        // Set the transform to move the SVG to the coordinate space adjustment
        this._svgElement.setAttribute('transform', transformText);
    } 
}

// Tell the browser about the new tag and the class it is linked to
customElements.define('touch-svg', TouchSvg);
